import type { ScoOption } from '~/pages/scorm/sco-navigation'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'

type Props = {
  scos: ScoOption[]
  active: string
  completed: ReadonlySet<string>
  onSelect: (identifier: string) => void
}

/**
 * Les chapitres d'un module à plusieurs SCO (#160), dans le bandeau du
 * lecteur : l'apprenant passe librement de l'un à l'autre, et voit ceux qu'il a
 * faits. Choisir ne suffit pas, il faut « Ouvrir » : parcourir la liste au
 * clavier déclenche un changement par option (RGAA 7.4), et chacun déchargerait
 * le chapitre en cours.
 */
function ScoPicker({ scos, active, completed, onSelect }: Props) {
  const { t } = useTranslation()
  const [picked, setPicked] = useState(active)
  useSyncOnChange(active, setPicked)

  return (
    <div className="flex min-w-0 items-center gap-2 text-sm" {...tourAnchor('player.chapters')}>
      <label className="flex min-w-0 items-center gap-2">
        <span className="shrink-0 text-muted-foreground">{t('scorm.chapters.label')}</span>
        <select
          value={picked}
          onChange={e => setPicked(e.target.value)}
          className="h-8 min-w-0 max-w-[16rem] truncate rounded-md border border-input bg-transparent px-2 text-sm"
        >
          {scos.map((sco, i) => (
            <option key={sco.identifier} value={sco.identifier}>
              {t(completed.has(sco.identifier) ? 'scorm.chapters.optionDone' : 'scorm.chapters.option', { position: i + 1, title: sco.title })}
            </option>
          ))}
        </select>
      </label>
      <Button type="button" variant="outline" size="sm" disabled={picked === active} onClick={() => onSelect(picked)}>
        {t('scorm.chapters.open')}
      </Button>
    </div>
  )
}

export default ScoPicker
